import React, { useState, useEffect } from 'react';

// 精密格式化逻辑
const formatMoney = (val) => {
  const num = parseFloat(val);
  if (isNaN(num)) return '--';
  if (num >= 10000) return `¥${(num / 10000).toFixed(2)}W`;
  return `¥${num.toFixed(2)}`;
};

const formatPnl = (val) => {
  if (val === '-' || isNaN(parseFloat(val))) return '--';
  const num = parseFloat(val);
  return num > 0 ? `+${num}%` : `${num}%`;
};

export default function App() {
  const [data, setData] = useState({ market: null, stocks: [] });
  const [loading, setLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState(null);
  const [modalChart, setModalChart] = useState(null);

  useEffect(() => {
    document.title = "量化决策系统";
    fetch('https://holy-violet-9cf1.serenityconle821.workers.dev')
      .then(res => res.json())
      .then(resData => {
        if (resData && resData.market && Array.isArray(resData.stocks)) {
          setData(resData);
        } else {
          setErrorStatus("数据结构异常，请检查Worker");
        }
        setLoading(false);
      })
      .catch(() => {
        setErrorStatus("网络连接异常");
        setLoading(false);
      });
  }, []);

  const theme = {
    bg: '#F5F5F7', surface: '#FFFFFF', textPrimary: '#1D1D1F', textSecondary: '#86868B',
    red: '#FF3B30', green: '#34C759', blue: '#007AFF', highlight: '#F0F7FF'
  };

  const getPnlColor = (val) => parseFloat(val) > 0 ? theme.red : (parseFloat(val) < 0 ? theme.green : theme.textPrimary);

  if (loading) return <div style={{padding:'40px', textAlign:'center'}}>初始化量化引擎...</div>;
  if (errorStatus) return <div style={{padding:'40px', textAlign:'center', color:theme.red}}>{errorStatus}</div>;

  return (
    <div style={{ backgroundColor: theme.bg, minHeight: '100vh', padding: '16px', fontFamily: '-apple-system, sans-serif' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '20px' }}>量化决策系统 <span style={{fontSize: '12px', color: theme.textSecondary, fontWeight: 'normal'}}>v14.9</span></h1>
        
        {/* 大盘详细数据 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
          {[{key:'sh',l:'上证'},{key:'sz',l:'深证'},{key:'cyb',l:'创业板'},{key:'ndq',l:'纳指'},{key:'dji',l:'道指'}].map(item => (
            <div key={item.key} style={{ background: theme.surface, padding: '12px', borderRadius: '12px', boxShadow:'0 2px 4px rgba(0,0,0,0.03)' }}>
              <div style={{ fontSize: '11px', color: theme.textSecondary }}>{item.l}</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{data.market[item.key]?.price}</div>
              <div style={{ fontSize: '12px', color: parseFloat(data.market[item.key]?.changePercent) > 0 ? theme.red : theme.green }}>{data.market[item.key]?.changePercent}%</div>
            </div>
          ))}
        </div>

        {/* 个股决策卡片 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {data.stocks.map(s => (
            <div key={s.code} style={{ background: theme.surface, padding: '20px', borderRadius: '16px', boxShadow:'0 4px 12px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
                
                {/* 1. 核心信息 */}
                <div style={{ flex: '1 1 200px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{s.name} <span style={{fontSize:'12px', color: theme.textSecondary}}>{s.code}</span></div>
                  <div style={{ fontSize: '26px', fontWeight: 'bold', color: getPnlColor(s.pnlDay), margin: '4px 0' }}>{formatMoney(s.currentPrice)}</div>
                </div>
                
                {/* 2. 盘口快照 (中文化) */}
                <div style={{ flex: '1 1 150px', fontSize: '12px' }}>
                  <div style={{color:theme.textSecondary, marginBottom:'4px'}}>盘口快照</div>
                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px'}}>
                    <div>今开: {formatMoney(s.open)}</div><div>昨收: {formatMoney(s.yesterdayClose)}</div>
                    <div>最高: {formatMoney(s.high)}</div><div>最低: {formatMoney(s.low)}</div>
                    <div>量比: {s.volumeRatio}</div><div>换手: {s.turnoverRate}</div>
                  </div>
                </div>

                {/* 3. 周期盈亏 */}
                <div style={{ flex: '1 1 120px', fontSize: '12px' }}>
                  <div style={{color:theme.textSecondary, marginBottom:'4px'}}>资产周期</div>
                  <div>月盈亏: <span style={{color: getPnlColor(s.pnlMTD)}}>{formatPnl(s.pnlMTD)}</span></div>
                  <div>年盈亏: <span style={{color: getPnlColor(s.pnlYTD)}}>{formatPnl(s.pnlYTD)}</span></div>
                </div>

                {/* 4. 策略逻辑 */}
                <div style={{ flex: '1 1 250px', background: theme.highlight, padding: '12px', borderRadius: '8px' }}>
                   <div style={{fontSize: '12px', fontWeight:'bold', color:theme.blue}}>量化纪律: {s.strategyType}</div>
                   <div style={{fontSize: '11px', marginTop:'4px', color: '#555'}}>{s.strategyDesc}</div>
                   <div style={{display:'flex', gap:'10px', marginTop:'6px', fontSize:'12px'}}>
                     <span style={{color:theme.blue, fontWeight:'600'}}>建议买入:{s.buyPrice}</span>
                     <span style={{color:theme.red, fontWeight:'600'}}>建议止盈:{s.sellPrice}</span>
                   </div>
                </div>

                {/* 5. K线 */}
                <div style={{ width: '100px', height: '70px', cursor: 'zoom-in' }} onClick={() => setModalChart(s.chartUrl)}>
                    <img src={s.chartUrl} style={{width:'100%', height:'100%', objectFit:'cover', borderRadius:'6px'}} alt="K线" />
                </div>
              </div>

              {/* 舆情 */}
              <div style={{ marginTop: '14px', borderTop: '1px solid #E5E5EA', paddingTop: '10px', fontSize: '13px', cursor: 'pointer', color: theme.blue }} onClick={() => window.open(s.newsLink, '_blank')}>
                <span style={{color: theme.textPrimary, fontWeight: '600'}}>AI 舆情摘要: </span>{s.newsSummary}
              </div>
            </div>
          ))}
        </div>
      </div>
      {modalChart && <div style={{position:'fixed', top:0, left:0, width:'100%', height:'100%', background:'rgba(0,0,0,0.8)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:999}} onClick={() => setModalChart(null)}><img src={modalChart} style={{maxWidth:'95%'}} alt="Full Chart" /></div>}
    </div>
  );
}
